import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Card from '../../common/Card';
import Button from '../../common/Button';
import Loading from '../../common/Loading';
import ErrorMessage from '../../common/ErrorMessage';
import useRepair from '../../../hooks/useRepair';
import useWarranty from '../../../hooks/useWarranty';
import { extractNumericId } from '../../../utils/formatters';
import './styles.css';

function RepairDetail({ token, user }) {
  const { repairId } = useParams();
  const navigate = useNavigate();
  
  // 使用自定義Hook
  const {
    fetchRepairById,
    updateRepairStatus,
    updateRepairDetails,
    updateLoading,
    updateError,
    formatDate
  } = useRepair(token);
  
  const { fetchWarrantyById } = useWarranty(token);

  // 本地狀態
  const [repair, setRepair] = useState(null);
  const [warranty, setWarranty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [completionDate, setCompletionDate] = useState('');
  const [newRepairCost, setNewRepairCost] = useState('');
  const [newRepairDetails, setNewRepairDetails] = useState('');

  // 處理照片 URL
  // 處理圖片URL
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;

    // 處理 Shopify MediaImage 格式的 URL (gid://shopify/MediaImage/xxxxx)
    if (imageUrl.startsWith('gid://shopify/MediaImage/')) {
      // 使用 /api/upload/file 端點來獲取圖片
      const uploadFileUrl = `/api/upload/file?id=${encodeURIComponent(imageUrl)}`;
      return uploadFileUrl;
    }

    // 如果已經是 Shopify CDN URL 格式，直接返回
    if (imageUrl.includes('cdn.shopify.com/s/files/')) {
      return imageUrl;
    }

    // 檢查URL是否為相對路徑或絕對路徑
    if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
      // 如果是相對路徑，添加完整的域名
      const baseUrl = window.location.origin;
      // 確保路徑格式正確
      const formattedPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
      return `${baseUrl}${formattedPath}`;
    }

    // 如果已經是完整URL，直接返回
    return imageUrl;
  };

  // 獲取維修單詳情
  useEffect(() => {
    if (!token || !repairId) return;

    setLoading(true);
    setError(null);

    fetchRepairById(`gid://shopify/Metaobject/${repairId}`)
      .then(data => {
        if (data) {
          setRepair(data);
          
          // 如果有保養單ID，獲取保養單詳情
          if (data.warranty_id) {
            return fetchWarrantyById(data.warranty_id);
          }
        }
        return null;
      })
      .then(warrantyData => {
        if (warrantyData) {
          setWarranty(warrantyData);
        }
      })
      .catch(err => {
        setError(err.message || '獲取維修單詳情時出錯');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token, repairId, fetchRepairById, fetchWarrantyById]);

  // 處理狀態更新
  const handleStatusUpdate = async () => {
    if (!newStatus && !newRepairCost && !newRepairDetails) return;

    try {
      // 檢查是否有修改
      const hasRepairCostUpdate = newRepairCost !== '';
      const hasStatusUpdate = newStatus !== '';
      const hasRepairDetailsUpdate = newRepairDetails !== '';

      // 分別處理狀態更新和維修費用更新
      if (hasStatusUpdate) {
        // 狀態更新
        const statusData = {
          status: newStatus
        };

        // 如果狀態是已完成，且有提供完成日期，則添加
        if (newStatus === 'Completed' && completionDate) {
          statusData.completionDate = completionDate;
        }
        
        console.log(`嘗試更新維修單 ${repair.id} 的狀態為 ${newStatus}`);

        const result = await updateRepairStatus(repair.id, statusData.status, statusData.completionDate);
        
        if (result) {
          console.log('維修單狀態更新成功:', result);
          setRepair(prev => ({
            ...prev,
            status: newStatus,
            completion_date: newStatus === 'Completed' ? completionDate : prev.completion_date
          }));
        } else {
          console.error('維修單狀態更新失敗: 返回結果為空');
        }
      }
      
      // 單獨處理維修費用，避免同時更新多個欄位
      if (hasRepairCostUpdate) {
        const costData = {
          repairCost: newRepairCost
        };
        
        console.log(`嘗試更新維修單費用為: ${newRepairCost}`);
        
        const costResult = await updateRepairDetails(repair.id, costData);
        
        if (costResult) {
          console.log('維修單費用更新成功:', costResult);
          setRepair(prev => ({
            ...prev,
            repair_cost: newRepairCost
          }));
        } else {
          console.error('維修單費用更新失敗: 返回結果為空');
        }
      }
      
      // 處理維修詳情更新
      if (hasRepairDetailsUpdate) {
        const detailsData = {
          repairDetails: newRepairDetails
        };
        
        console.log(`嘗試更新維修單詳情`);
        
        const detailsResult = await updateRepairDetails(repair.id, detailsData);
        
        if (detailsResult) {
          console.log('維修單詳情更新成功:', detailsResult);
          setRepair(prev => ({
            ...prev,
            repair_details: newRepairDetails
          }));
        } else {
          console.error('維修單詳情更新失敗: 返回結果為空');
        }
      }
      
      setShowStatusForm(false);
      setNewRepairCost('');
      setNewStatus('');
      setNewRepairDetails('');
      
    } catch (err) {
      console.error('更新維修單時出錯:', err);
      setError(`更新維修單時出錯: ${err.message}`);
    }
  };

  // 渲染狀態標籤
  const renderStatusBadge = (status) => {
    const statusMap = {
      'Pending': '待處理',
      'In Progress': '處理中',
      'Completed': '已完成',
      'Cancelled': '已取消'
    };

    const statusClass = {
      'Pending': 'status-pending',
      'In Progress': 'status-in-progress',
      'Completed': 'status-completed',
      'Cancelled': 'status-cancelled'
    };

    return (
      <span className={`repair-status-badge ${statusClass[status] || 'status-pending'}`}>
        {statusMap[status] || status}
      </span>
    );
  };

  // 解析富文本格式JSON
  const parseRichText = (text) => {
    if (!text) return '暫無信息';
    
    try {
      // 嘗試解析JSON
      const richText = JSON.parse(text);
      
      // 檢查是否是富文本格式
      if (richText && richText.type === 'root' && richText.children && richText.children.length > 0) {
        // 提取文本內容
        let extractedText = '';
        const extractText = (nodes) => {
          if (!nodes || !nodes.length) return;
          
          nodes.forEach(node => {
            if (node.type === 'text' && node.value) {
              extractedText += node.value;
            }
            if (node.children) {
              extractText(node.children);
            }
          });
        };
        
        extractText(richText.children);
        return extractedText || '暫無信息';
      }
      
      // 如果不是富文本格式，直接返回原始字符串
      return text;
    } catch (e) {
      // 如果解析失敗，直接返回原始字符串
      return text;
    }
  };

  // 加載中狀態
  if (loading) {
    return <Loading text="正在加載維修單詳情..." />;
  }

  // 錯誤狀態
  if (error) {
    return <ErrorMessage message={error} />;
  }

  // 維修單不存在
  if (!repair) {
    return (
      <div className="repair-detail-container">
        <Card className="repair-detail-card">
          <div className="repair-not-found">
            <h2>維修單不存在</h2>
            <p>找不到指定的維修單，或者您沒有查看該維修單的權限。</p>
            <Button onClick={() => navigate('/repairs')}>
              返回維修單列表
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="repair-detail-container">
      <Card className="repair-detail-card">
        <div className="repair-detail-header">
          <div className="repair-detail-title-section">
            <h2 className="repair-detail-title">維修單詳情</h2>
            {renderStatusBadge(repair.status)}
          </div>
          
          <div className="repair-detail-actions">
            <Button onClick={() => navigate('/repairs')} className="back-btn">
              返回列表
            </Button>
            <Button onClick={() => setShowStatusForm(!showStatusForm)} className="update-btn">
              更新狀態
            </Button>
          </div>
        </div>

        {/* 狀態更新表單 */}
        {showStatusForm && (
          <div className="repair-status-form">
            <h3>更新維修單</h3>
            
            <div className="form-group">
              <label>新狀態</label>
              <select 
                value={newStatus} 
                onChange={(e) => setNewStatus(e.target.value)}
                className="status-select"
              >
                <option value="">選擇狀態</option>
                <option value="Pending">待處理</option>
                <option value="In Progress">處理中</option>
                <option value="Completed">已完成</option>
                <option value="Cancelled">已取消</option>
              </select>
            </div>
            
            {newStatus === 'Completed' && (
              <div className="form-group">
                <label>完成日期</label>
                <input 
                  type="date" 
                  value={completionDate} 
                  onChange={(e) => setCompletionDate(e.target.value)}
                  className="date-input"
                />
              </div>
            )}
            
            <div className="form-group">
              <label>維修費用</label>
              <input 
                type="text" 
                value={newRepairCost} 
                onChange={(e) => setNewRepairCost(e.target.value)}
                placeholder="例如：HK$500"
                className="form-input"
              />
            </div>
            
            <div className="form-group">
              <label>維修詳情</label>
              <textarea 
                value={newRepairDetails} 
                onChange={(e) => setNewRepairDetails(e.target.value)}
                placeholder="更新維修詳情..."
                className="form-textarea"
                rows={4}
              />
            </div>
            
            <div className="form-actions">
              <Button 
                onClick={() => setShowStatusForm(false)} 
                className="cancel-status-btn"
              >
                取消
              </Button>
              <Button 
                onClick={handleStatusUpdate} 
                className="submit-status-btn"
                disabled={(!newStatus && !newRepairCost && !newRepairDetails) || updateLoading}
              >
                {updateLoading ? '更新中...' : '更新'}
              </Button>
            </div>
            
            {updateError && (
              <div className="status-update-error">
                更新失敗: {updateError}
              </div>
            )}
          </div>
        )}

        {/* 維修單基本信息 */}
        <div className="repair-info-section">
          <div className="repair-info-group">
            <div className="repair-info-item">
              <span className="info-label">維修單編號</span>
              <span className="info-value">{extractNumericId(repair.id)}</span>
            </div>
            
            <div className="repair-info-item">
              <span className="info-label">創建日期</span>
              <span className="info-value">{formatDate(repair.creation_date)}</span>
            </div>
            
            {repair.status === 'Completed' && repair.completion_date && (
              <div className="repair-info-item">
                <span className="info-label">完成日期</span>
                <span className="info-value">{formatDate(repair.completion_date)}</span>
              </div>
            )}
            
            <div className="repair-info-item">
              <span className="info-label">技術員</span>
              <span className="info-value">{repair.technician || '未指派'}</span>
            </div>
            
            {repair.repair_cost && (
              <div className="repair-info-item">
                <span className="info-label">維修費用</span>
                <span className="info-value">{repair.repair_cost}</span>
              </div>
            )}
          </div>
          
          {/* 保養單信息 */}
          {warranty && (
            <div className="related-warranty">
              <h3>關聯保養單</h3>
              <div className="warranty-info">
                <div className="warranty-info-item">
                  <span className="info-label">保養單編號</span>
                  <span className="info-value">
                    <Link to={`/warranties/${extractNumericId(warranty.id)}`}>
                      {extractNumericId(warranty.id)}
                    </Link>
                  </span>
                </div>
                
                <div className="warranty-info-item">
                  <span className="info-label">產品名稱</span>
                  <span className="info-value">{warranty.product_name || '未設置'}</span>
                </div>
                
                <div className="warranty-info-item">
                  <span className="info-label">購買日期</span>
                  <span className="info-value">{formatDate(warranty.purchase_date)}</span>
                </div>
                
                <div className="warranty-info-item">
                  <span className="info-label">保養狀態</span>
                  <span className="info-value">{warranty.verification_status}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 診斷和維修詳情 */}
        <div className="repair-details-section">
          <div className="detail-block">
            <h3>初步診斷</h3>
            <div className="detail-content">
              {parseRichText(repair.initial_diagnosis)}
            </div>
          </div>
          
          <div className="detail-block">
            <h3>維修詳情</h3>
            <div className="detail-content">
              {parseRichText(repair.repair_details)}
            </div>
          </div>
        </div>

        {/* 附加照片 */}
        {repair.attached_photos && (
          <div className="repair-photos-section">
            <h3>附加照片</h3>
            <div className="photos-grid">
              {(() => {
                try {
                  const photoIds = JSON.parse(repair.attached_photos);
                  return photoIds.length > 0 ? (
                    photoIds.map((photoId, index) => {
                      // 判斷是否為 Shopify MediaImage 格式
                      const isShopifyMediaImage = photoId.startsWith && photoId.startsWith('gid://shopify/MediaImage/');
                      const imageUrl = getImageUrl(photoId);

                      return (
                        <div key={index} className="photo-item">
                          {isShopifyMediaImage ? (
                            <div className="repair-photo-container">
                              <div style={{ marginBottom: '10px', textAlign: 'center' }}>
                                <a
                                  href={imageUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'inline-block',
                                    padding: '6px 12px',
                                    backgroundColor: '#4CAF50',
                                    color: 'white',
                                    textDecoration: 'none',
                                    borderRadius: '4px',
                                    fontWeight: 'bold',
                                    fontSize: '14px'
                                  }}
                                >
                                  點擊查看圖片
                                </a>
                              </div>
                              <img
                                src={imageUrl}
                                alt={`維修照片 ${index + 1}`}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.style.display = 'none';
                                  // 顯示錯誤信息
                                  const placeholder = document.createElement('div');
                                  placeholder.innerHTML = `
                                    <div style="
                                      width: 100%;
                                      height: 150px;
                                      background-color: #f5f5f5;
                                      display: flex;
                                      flex-direction: column;
                                      justify-content: center;
                                      align-items: center;
                                      border-radius: 4px;
                                      padding: 10px;
                                      text-align: center
                                    ">
                                      <p style="margin-bottom: 10px">無法顯示圖片</p>
                                      <p style="font-size: 0.9em; color: #666">請點擊上方按鈕查看</p>
                                    </div>
                                  `;
                                  e.target.parentNode.appendChild(placeholder);
                                }}
                                style={{ maxWidth: '100%', maxHeight: '200px', display: 'block', margin: '0 auto' }}
                              />
                            </div>
                          ) : (
                            <img
                              src={imageUrl}
                              alt={`維修照片 ${index + 1}`}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjOTk5Ij7lm77niYfliqDovb3lpLHotKU8L3RleHQ+PC9zdmc+';
                                e.target.style.opacity = '0.7';
                              }}
                              style={{ maxWidth: '100%', maxHeight: '200px' }}
                            />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p>無附加照片</p>
                  );
                } catch (e) {
                  console.error('解析照片數據出錯:', e);
                  return <p>照片數據格式錯誤</p>;
                }
              })()}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

export default RepairDetail; 